import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { api } from '@api/v1/client'
import DataGrid from '@components/common/DataGrid'
import LoadingSpinner from '@components/common/LoadingSpinner'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const TABS = ['Overview', 'Distributions', 'History']

const fmtCurrency = (v) => {
  const n = Number(v)
  if (isNaN(n)) return '$0.00'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const StatusBadge = ({ status }) => {
  const colors = {
    succeeded: 'text-emerald-700 bg-emerald-50',
    approved: 'text-emerald-700 bg-emerald-50',
    settled: 'text-emerald-700 bg-emerald-50',
    pending: 'text-amber-700 bg-amber-50',
    processing: 'text-blue-700 bg-blue-50',
    failed: 'text-red-700 bg-red-50',
    reversed: 'text-red-700 bg-red-50',
    refunded: 'text-red-700 bg-red-50',
  }
  const key = (status || 'pending').toLowerCase().replace(/\s+/g, '_')
  const cls = colors[key] || 'text-gray-600 bg-gray-100'
  return (
    <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${cls}`}>
      {(status || 'Unknown').replace(/_/g, ' ')}
    </span>
  )
}

const DetailRow = ({ label, value, highlight }) => (
  <div className="flex items-start py-2.5 border-b border-gray-50 last:border-0">
    <dt className="w-44 shrink-0 text-sm text-gray-500">{label}</dt>
    <dd className={`text-sm ${highlight ? 'font-semibold text-gray-900' : 'text-gray-900'}`}>
      {value || '--'}
    </dd>
  </div>
)

/*
 * Lifted out of the two places that already drew it, because CC-53132 adds two
 * more dead-end screens (paused / failed) that need the same way out. The markup
 * and the classes are unchanged — `className` carries the only thing that ever
 * differed between the two original copies, the bottom margin.
 */
const BackToPayments = ({ onClick, className = 'mb-4' }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 ${className}`}
  >
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
    Back to Payments
  </button>
)

const PaymentDetail = () => {
  useDocumentTitle('Payment Detail')
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('Overview')

  /*
   * CC-53132 — a read has three outcomes, not two. See `@api/v1/readState`.
   *
   * This page used to read `isLoading` off the payment query and treat anything
   * else as "loaded". `isLoading` is `isPending && isFetching`, so it is FALSE
   * for a query that is pending but NOT in flight — above all a PAUSED one
   * (react-query's default `networkMode: 'online'` refuses to fire while
   * `onlineManager` believes the browser is offline). `queryFn` is never called,
   * nothing throws, nothing logs, `data` stays `undefined`, and `payment` below
   * therefore falls all the way to `null`.
   *
   * The screen that produced was **"Payment not found"** — about a payment that
   * demonstrably exists, with no spinner, no error and no request anywhere in a
   * log to explain it. It is the same lie ReportView used to tell about a
   * report, and worse here: a firm chasing a reversed or disputed transfer is
   * being told the record is gone. A FAILED read told the identical story, for a
   * different reason — this page never rendered `error` at all, so there was no
   * error branch to reach and every failure fell through to "not found" too.
   *
   * `useReadState` rather than the bare `readState` because it carries the
   * permanent blocked-read diagnostic: a paused fetch leaves no trace on either
   * side by construction, so the console warning is the only evidence there will
   * ever be.
   */
  const paymentQuery = useQuery({
    queryKey: ['payment', id],
    queryFn: () => api.get(`/objects/1009/${id}`),
  })
  const paymentState = useReadState(`payment:${id}`, paymentQuery)
  const { data: paymentResp } = paymentQuery

  /*
   * Distributions are money — every row carries an Amount, Fees and Expenses,
   * and the grid exports them. So "No distributions found for this payment" is a
   * financial claim about how this payment was applied, and it may only be made
   * about a read that finished.
   *
   * `enabled` makes this query genuinely conditional, so unlike the one above
   * `idle` is a REAL state here. The per-page decision: leave it as the ordinary
   * "not asked yet" — do not surface it as an error, and do NOT fold it into the
   * loading branch.
   *
   * It is also not reachable at the one place this read is rendered, which is
   * worth writing down because it is not obvious. The grid's mount condition and
   * this `enabled` condition are the SAME boolean, and on the render where that
   * boolean flips react-query's optimistic result already reports `fetching`
   * (or `paused`, offline) — never `idle`. If someone later decouples the two,
   * the grid's own honest `unresolvedMessage` catches it, so no state renders a
   * blank; folding idle into `distribLoading` instead would have given that
   * person a permanent spinner on the tab.
   */
  const distribQuery = useQuery({
    queryKey: ['payment-distrib', id],
    queryFn: () => api.get('/objects/33', { limit: 200 }),
    enabled: activeTab === 'Distributions',
  })
  const distribState = useReadState(`payment-distrib:${id}`, distribQuery)
  const { data: distribResp, isLoading: distribLoading } = distribQuery

  const payment = paymentResp?.data || paymentResp || null
  const fields = paymentResp?.meta?.fields || []

  // Parse fields into structured data
  const parsed = useMemo(() => {
    if (!payment) return {}
    const result = { id: payment.id, name: payment.name }
    const fieldMap = {}
    for (const f of fields) fieldMap[f.id] = f

    for (const [fid, val] of Object.entries(payment)) {
      if (fid === 'id' || fid === 'name') continue
      const field = fieldMap[fid]
      if (!field) continue
      const lbl = (field.label || '').toLowerCase()

      if (lbl.includes('amount') && !lbl.includes('fee') && !lbl.includes('unapplied')) {
        result.amount = Number(val) || 0
      }
      if (lbl.includes('unapplied')) result.unappliedBalance = Number(val) || 0
      if (lbl.includes('date') && lbl.includes('received')) result.dateReceived = val
      if (lbl === 'payment type' || lbl === 'type') result.paymentType = String(val || '')
      if (lbl.includes('reference')) result.refNum = String(val || '')
      if (lbl.includes('contact') || lbl.includes('received from')) {
        result.contact = String(val || '')
      }
      if (lbl.includes('company')) result.company = String(val || '')
      if (lbl.includes('matter') || lbl.includes('case')) result.matter = String(val || '')
      if (lbl.includes('convenience') && lbl.includes('fee')) {
        result.convFee = Number(val) || 0
      }
      if (lbl.includes('trust')) result.isTrust = Boolean(val)
      if (lbl.includes('reversal') && !lbl.includes('of')) result.isReversal = Boolean(val)
      if (lbl.includes('reversal') && lbl.includes('of')) result.reversalOf = String(val || '')
      if (lbl.includes('deposit') && !lbl.includes('trust')) result.deposit = String(val || '')
      if (lbl.includes('account') && !lbl.includes('trust')) result.account = String(val || '')
      if (lbl.includes('approved') && lbl.includes('date')) result.approvedDate = val
      if (lbl.includes('settled')) result.settled = Boolean(val)
      if (lbl.includes('funded')) result.funded = Boolean(val)
      if (lbl.includes('category')) result.category = String(val || '')
      if (lbl.includes('sub') && lbl.includes('type')) result.subType = String(val || '')
    }

    // Determine status
    if (result.isReversal) result.status = 'reversed'
    else if (result.funded) result.status = 'funded'
    else if (result.settled) result.status = 'settled'
    else if (result.approvedDate) result.status = 'approved'
    else result.status = 'succeeded'

    // Store all raw fields for the detail display
    result.allFields = []
    for (const [fid, val] of Object.entries(payment)) {
      if (fid === 'id' || fid === 'name') continue
      const field = fieldMap[fid]
      if (field && val != null && val !== '') {
        result.allFields.push({ label: field.label, value: val })
      }
    }

    return result
  }, [payment, fields])

  // Distribution columns
  const distribColumns = useMemo(() => [
    {
      id: 'date',
      header: 'Date',
      accessorKey: 'distributionDate',
      size: 110,
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? dayjs(v).format('M/D/YYYY') : '--'
      },
    },
    { id: 'name', header: 'Description', accessorKey: 'name', size: 220 },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      size: 130,
      cell: ({ getValue }) => (
        <span className="font-medium">{fmtCurrency(getValue())}</span>
      ),
    },
    {
      id: 'fees',
      header: 'Fees',
      accessorKey: 'feeTotal',
      size: 110,
      cell: ({ getValue }) => fmtCurrency(getValue()),
    },
    {
      id: 'expenses',
      header: 'Expenses',
      accessorKey: 'expenseTotal',
      size: 110,
      cell: ({ getValue }) => fmtCurrency(getValue()),
    },
    {
      id: 'isMain',
      header: 'Primary',
      accessorKey: 'isMainDistribution',
      size: 90,
      cell: ({ getValue }) => getValue() ? (
        <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
          Yes
        </span>
      ) : '--',
    },
  ], [])

  /*
   * `idle` rides along with `loading` deliberately. The payment query carries no
   * `enabled` option, so it cannot reach that state today — but every branch
   * below is gated on a NAMED state and the last one falls through to "Payment
   * not found", so an unhandled fifth state would hand whoever adds an `enabled`
   * gate here later exactly the lie this block exists to remove.
   */
  if (paymentState === 'loading' || paymentState === 'idle') {
    return <LoadingSpinner fullScreen message="Loading payment..." />
  }

  /*
   * Paused. No request was sent, so nothing on this screen would be describing
   * this payment — which is precisely why it must not look like the "not found"
   * panel below, and must not look like the loaded page with zeroes in it
   * either. `role="alert"` because the thing this replaces asserted the record
   * does not exist, and the user has to be told that is not what happened.
   * "Try again" refetches: the page is fine, the connection is not, so making
   * them reload the whole app would be beside the point.
   */
  if (paymentState === 'blocked') {
    return (
      <div className="max-w-7xl mx-auto">
        <BackToPayments onClick={() => navigate('/payments')} className="mb-6" />
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Can’t reach the server, so we haven’t loaded this payment.</p>
          <p className="mt-1 text-amber-800">
            This does not mean the payment is missing — your connection looks offline and the
            record was never read, so no amount, status or balance is being shown. Check the
            connection and try again.
          </p>
          <button
            type="button"
            onClick={() => paymentQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  /*
   * Failed. This had no render site at all before — the read threw, `payment`
   * came out `null`, and the user was told the payment does not exist. The
   * error message goes on screen because a 502 from the MVC bridge and a
   * genuinely deleted record are different problems with different owners.
   */
  if (paymentState === 'failed') {
    return (
      <div className="max-w-7xl mx-auto">
        <BackToPayments onClick={() => navigate('/payments')} className="mb-6" />
        <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
          <p className="font-medium">We couldn’t load this payment: {paymentQuery.error?.message}</p>
          <p className="mt-1">
            This is not a missing payment — the read failed, so nothing here describes the
            transaction.
          </p>
          <button
            type="button"
            onClick={() => paymentQuery.refetch()}
            className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  /* Reached only on a COMPLETED read that came back with nothing — which is the
     one circumstance in which "Payment not found" is a fact rather than a guess. */
  if (!payment) {
    return (
      <div className="max-w-7xl mx-auto">
        <BackToPayments onClick={() => navigate('/payments')} className="mb-6" />
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-sm text-gray-500">Payment not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <BackToPayments onClick={() => navigate('/payments')} />

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-display text-gray-900">
              {parsed.name || `Payment ${id}`}
            </h1>
            <StatusBadge status={parsed.status} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {parsed.dateReceived ? dayjs(parsed.dateReceived).format('MMMM D, YYYY') : ''}
            {parsed.refNum ? ` \u2022 Ref: ${parsed.refNum}` : ''}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Amount</p>
          <p className="text-3xl font-semibold text-gray-900 font-display">
            {fmtCurrency(parsed.amount)}
          </p>
          {parsed.convFee > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              + {fmtCurrency(parsed.convFee)} convenience fee
            </p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-xs font-medium text-gray-500 mb-0.5">Payment Type</p>
          <p className="text-sm font-semibold text-gray-900">
            {parsed.paymentType || '--'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-xs font-medium text-gray-500 mb-0.5">Account</p>
          <p className="text-sm font-semibold text-gray-900">
            {parsed.isTrust ? 'Trust' : 'Operating'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-xs font-medium text-gray-500 mb-0.5">Unapplied Balance</p>
          <p className="text-sm font-semibold text-gray-900">
            {fmtCurrency(parsed.unappliedBalance)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <p className="text-xs font-medium text-gray-500 mb-0.5">Settlement</p>
          <p className="text-sm font-semibold text-gray-900">
            {parsed.funded ? 'Funded' : parsed.settled ? 'Settled' : 'Pending'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'text-cb-accent-text border-cb-accent'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'Overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Payment Details</h2>
            <dl>
              <DetailRow label="Amount" value={fmtCurrency(parsed.amount)} highlight />
              <DetailRow label="Date Received" value={
                parsed.dateReceived ? dayjs(parsed.dateReceived).format('MMMM D, YYYY') : null
              } />
              <DetailRow label="Payment Type" value={parsed.paymentType} />
              <DetailRow label="Reference Number" value={parsed.refNum} />
              <DetailRow label="Sub-Type" value={parsed.subType} />
              <DetailRow label="Category" value={parsed.category} />
              <DetailRow label="Deposit" value={parsed.deposit} />
            </dl>
          </div>

          {/* Client & Matter */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Client & Matter</h2>
            <dl>
              <DetailRow label="Received From" value={parsed.contact} />
              <DetailRow label="Company" value={parsed.company} />
              <DetailRow label="Matter" value={parsed.matter} />
              <DetailRow label="Account" value={parsed.account} />
              <DetailRow label="Trust Deposit" value={parsed.isTrust ? 'Yes' : 'No'} />
            </dl>
          </div>

          {/* Financial Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Financial Status</h2>
            <dl>
              <DetailRow label="Convenience Fee" value={
                parsed.convFee ? fmtCurrency(parsed.convFee) : 'None'
              } />
              <DetailRow label="Unapplied Balance" value={fmtCurrency(parsed.unappliedBalance)} />
              <DetailRow label="Approved Date" value={
                parsed.approvedDate ? dayjs(parsed.approvedDate).format('M/D/YYYY h:mm A') : null
              } />
              <DetailRow label="Settled" value={parsed.settled ? 'Yes' : 'No'} />
              <DetailRow label="Funded" value={parsed.funded ? 'Yes' : 'No'} />
            </dl>
          </div>

          {/* Reversal Info (if applicable) */}
          {(parsed.isReversal || parsed.reversalOf) && (
            <div className="bg-red-50 rounded-xl border border-red-100 p-5">
              <h2 className="text-sm font-semibold text-red-900 mb-3">Reversal Information</h2>
              <dl>
                <DetailRow label="Is Reversal" value={parsed.isReversal ? 'Yes' : 'No'} />
                <DetailRow label="Reversal Of" value={parsed.reversalOf} />
              </dl>
            </div>
          )}

          {/* All Fields (raw) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">All Fields</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
              {parsed.allFields?.map((f, i) => (
                <div key={i} className="flex items-start py-2 border-b border-gray-50">
                  <dt className="w-36 shrink-0 text-xs text-gray-500">{f.label}</dt>
                  <dd className="text-xs text-gray-900 break-all">
                    {typeof f.value === 'boolean' ? (f.value ? 'Yes' : 'No') : String(f.value)}
                  </dd>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Distributions Tab */}
      {activeTab === 'Distributions' && (
        <>
          {/* Paused. `DataGrid` already refuses to say "No distributions found"
              for an unresolved read and already withholds the row count — that is
              its default now — but its honest message carries no way out. This is
              a money list on a transaction detail page, so the user gets the
              retry here rather than being left to guess at a reload. */}
          {distribState === 'blocked' && (
            <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
              <p className="font-medium">Can’t reach the server, so we haven’t loaded the distributions.</p>
              <p className="mt-1 text-amber-800">
                This is not a payment with no distributions — the list was never read, so how
                this payment was applied is simply unknown right now. Your connection looks
                offline; check it and try again.
              </p>
              <button
                type="button"
                onClick={() => distribQuery.refetch()}
                className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                Try again
              </button>
            </div>
          )}

          {/* Failed. Also had no render site before: this query's `error` was
              destructured away and the grid was handed `[]`, so a 500 read as
              "this payment has no distributions". */}
          {distribState === 'failed' && (
            <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
              <p className="font-medium">We couldn’t load the distributions: {distribQuery.error?.message}</p>
              <p className="mt-1">
                This is not a payment with no distributions — nothing was read, so nothing here
                accounts for the amounts, fees or expenses.
              </p>
              <button
                type="button"
                onClick={() => distribQuery.refetch()}
                className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
              >
                Try again
              </button>
            </div>
          )}

          {/* `data` is `distribResp?.data` and NOT `… || []`: collapsing
              `undefined` to `[]` is the defect itself, and the grid can only tell
              "the server said nothing" from "nobody asked" if the difference
              survives the trip. */}
          {distribState !== 'blocked' && distribState !== 'failed' && (
            <DataGrid
              columns={distribColumns}
              data={distribResp?.data}
              isLoading={distribLoading}
              emptyMessage="No distributions found for this payment"
              unresolvedMessage="These distributions were not read, so this is not a payment with none. Reload to try again."
              loadingMessage="Loading distributions..."
              exportFilename={`payment-${id}-distributions`}
              pageSize={25}
            />
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'History' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Payment Timeline</h2>
          <div className="relative pl-6 space-y-6">
            <div className="absolute left-2 top-1 bottom-1 w-px bg-gray-200" />

            {/* Created */}
            <div className="relative">
              <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-cb-accent border-2 border-white shadow-sm" />
              <p className="text-sm font-medium text-gray-900">Payment Created</p>
              <p className="text-xs text-gray-500">
                {parsed.dateReceived
                  ? dayjs(parsed.dateReceived).format('MMM D, YYYY [at] h:mm A')
                  : 'Date unknown'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fmtCurrency(parsed.amount)} via {parsed.paymentType || 'Unknown'}
              </p>
            </div>

            {/* Approved */}
            {parsed.approvedDate && (
              <div className="relative">
                <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow-sm" />
                <p className="text-sm font-medium text-gray-900">Payment Approved</p>
                <p className="text-xs text-gray-500">
                  {dayjs(parsed.approvedDate).format('MMM D, YYYY [at] h:mm A')}
                </p>
              </div>
            )}

            {/* Settled */}
            {parsed.settled && (
              <div className="relative">
                <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-sm" />
                <p className="text-sm font-medium text-gray-900">Settled</p>
                <p className="text-xs text-gray-500">Funds transferred to bank</p>
              </div>
            )}

            {/* Funded */}
            {parsed.funded && (
              <div className="relative">
                <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow-sm" />
                <p className="text-sm font-medium text-gray-900">Funded</p>
                <p className="text-xs text-gray-500">Funds deposited to account</p>
              </div>
            )}

            {/* Reversed */}
            {parsed.isReversal && (
              <div className="relative">
                <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow-sm" />
                <p className="text-sm font-medium text-gray-900">Reversed</p>
                <p className="text-xs text-gray-500">Payment has been reversed</p>
                {parsed.reversalOf && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Original payment: {parsed.reversalOf}
                  </p>
                )}
              </div>
            )}

            {/* Current Status */}
            <div className="relative">
              <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-gray-300 border-2 border-white shadow-sm" />
              <p className="text-sm font-medium text-gray-500">Current Status</p>
              <StatusBadge status={parsed.status} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentDetail
